import { create } from 'zustand';
import type { FlowCueStateV1 } from '@/types/player/perform-control-v1.types';
import type { LiveNextCueTarget } from '@/helpers/resolve-next-cue-preview-row.helper';
import type { SimulationCuePlaylistContext } from '@/helpers/simulation-cue-engine.helper';
import {
  createNextCueRecalculatingLatch,
  type NextCueRecalculatingLatch,
} from '@/helpers/resolve-next-cue-recalculating.helper';

type LatchJumpParams = {
  entityRef: string;
  cueState: FlowCueStateV1 | null | undefined;
  targetItemId?: string;
  targetContext?: SimulationCuePlaylistContext;
  targetExpandId?: string;
  expectedNext?: LiveNextCueTarget;
  expectedNextExpandId?: string;
};

type NextCueRecalculatingSlice = {
  latchByEntityRef: Record<string, NextCueRecalculatingLatch>;
  mutationFailedByEntityRef: Record<string, boolean>;
  latchJump: (params: LatchJumpParams) => void;
  markJumpFailed: (entityRef: string) => void;
  clearLatch: (entityRef: string) => void;
};

export const useNextCueRecalculatingStore = create<NextCueRecalculatingSlice>((set) => ({
  latchByEntityRef: {},
  mutationFailedByEntityRef: {},
  latchJump: ({
    entityRef,
    cueState,
    targetItemId,
    targetContext,
    targetExpandId,
    expectedNext,
    expectedNextExpandId,
  }) => {
    set((state) => ({
      latchByEntityRef: {
        ...state.latchByEntityRef,
        [entityRef]: createNextCueRecalculatingLatch({
          entityRef,
          cueState,
          targetItemId,
          targetContext,
          targetExpandId,
          expectedNext,
          expectedNextExpandId,
        }),
      },
      mutationFailedByEntityRef: {
        ...state.mutationFailedByEntityRef,
        [entityRef]: false,
      },
    }));
  },
  markJumpFailed: (entityRef) => {
    set((state) => ({
      mutationFailedByEntityRef: {
        ...state.mutationFailedByEntityRef,
        [entityRef]: true,
      },
    }));
  },
  clearLatch: (entityRef) => {
    set((state) => {
      if (!state.latchByEntityRef[entityRef] && !state.mutationFailedByEntityRef[entityRef]) {
        return state;
      }
      const latchByEntityRef = { ...state.latchByEntityRef };
      const mutationFailedByEntityRef = { ...state.mutationFailedByEntityRef };
      delete latchByEntityRef[entityRef];
      delete mutationFailedByEntityRef[entityRef];
      return { latchByEntityRef, mutationFailedByEntityRef };
    });
  },
}));

export function latchNextCueRecalculatingForJump(params: LatchJumpParams): void {
  useNextCueRecalculatingStore.getState().latchJump(params);
}

export function markNextCueRecalculatingJumpFailed(entityRef: string): void {
  useNextCueRecalculatingStore.getState().markJumpFailed(entityRef);
}
